import { useId, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/cn';
import { FieldLabel } from './airport-ui';

// A tiny, dependency-free collapsible Card for the PNR operation center. The
// `@radix-ui/react-collapsible` package is deliberately NOT added (absent from
// the lockfile; the CI bundle-budget gate would reject it) — this is a plain
// `useState` toggle with the proper `aria-expanded`/`aria-controls`/`role=region`
// wiring so it stays accessible. The header is a full-width button; the chevron
// rotates 180° when open.

interface CollapsibleCardProps {
  /** Section label (e.g. "Passengers", "Activity"). */
  title: string;
  /** Optional leading icon (lucide); rendered aria-hidden. */
  icon?: ReactNode;
  /** Optional trailing badge/count next to the title. */
  badge?: ReactNode;
  /** Optional content pinned to the right of the header, left of the chevron. */
  headerRight?: ReactNode;
  /** Start expanded. Defaults to collapsed (secondary detail). */
  defaultOpen?: boolean;
  children: ReactNode;
}

export function CollapsibleCard({
  title,
  icon,
  badge,
  headerRight,
  defaultOpen = false,
  children,
}: CollapsibleCardProps) {
  const [open, setOpen] = useState(defaultOpen);
  const contentId = useId();
  const headerId = useId();

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        id={headerId}
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-muted/50"
      >
        {icon ? <span className="shrink-0 text-muted-foreground">{icon}</span> : null}
        <FieldLabel>{title}</FieldLabel>
        {badge ? <span className="shrink-0">{badge}</span> : null}
        <span className="ml-auto flex shrink-0 items-center gap-2">
          {headerRight}
          <ChevronDown
            className={cn('h-4 w-4 text-muted-foreground transition-transform', open && 'rotate-180')}
            aria-hidden
          />
        </span>
      </button>
      {open ? (
        <div
          id={contentId}
          role="region"
          aria-labelledby={headerId}
          className="border-t border-border px-3 py-3"
        >
          {children}
        </div>
      ) : null}
    </Card>
  );
}
